import { useAxios } from 'hooks/useAxios';
import { AxiosErrorHandler } from 'shared/Helpers';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  IBannerAd,
  CreateBannerAd,
  GlobalRateLimitsResponse,
  ClientRateLimitsResponse,
  SaveGlobalRateLimitsPayload,
  SaveClientEndpointOverridePayload,
  SaveClientEndpointExceptionsPayload,
  SaveClientRateLimitsResponse
} from 'types/SiteAdministration';
import { BASE_URL_V2 } from 'shared/constants';

export const useSiteAdministrationApi = () => {
  const { axios } = useAxios(BASE_URL_V2);

  const fetchBannerAds = async (all: boolean): Promise<IBannerAd[]> => {
    try {
      const endpoint = all ? '/banners?all=true' : '/banners';
      const res = await axios.get(endpoint);
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to fetch banner ads'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const createBannerAd = async (banner: FormData): Promise<IBannerAd> => {
    try {
      const res = await axios.post(`/banners/banner`, banner, {
        transformRequest: () => banner
      });
      if (res.status === 201) {
        return res.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to create banner ad'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const editBannerAd = async (id: number, payload: Partial<CreateBannerAd>): Promise<IBannerAd> => {
    try {
      const res = await axios.patch(`/banners/${id}`, payload);
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to edit banner ad'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const deleteBannerAd = async (id: number): Promise<void> => {
    try {
      const res = await axios.delete(`/banners/${id}`);
      if (res.status === 200) {
        return;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to delete banner ad'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const fetchGlobalApiLimits = async (): Promise<GlobalRateLimitsResponse> => {
    try {
      const res = await axios.get('/admin/rate-limits/global');
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to fetch global API limits'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const saveGlobalApiLimits = async (payload: SaveGlobalRateLimitsPayload): Promise<any> => {
    try {
      const res = await axios.put('/admin/rate-limits/global', payload);
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to save global API limits'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const fetchClientApiLimits = async (clientId: number): Promise<ClientRateLimitsResponse> => {
    try {
      const res = await axios.get(`/admin/rate-limits/clients/${clientId}`);
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to fetch client API limits'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const saveClientEndpointOverride = async (
    clientId: number,
    endpointId: number,
    payload: SaveClientEndpointOverridePayload
  ): Promise<SaveClientRateLimitsResponse> => {
    try {
      const res = await axios.put(`/admin/rate-limits/clients/${clientId}/endpoints/${endpointId}/override`, payload);
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to save client endpoint override'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const saveClientEndpointExceptions = async (
    clientId: number,
    endpointId: number,
    payload: SaveClientEndpointExceptionsPayload
  ): Promise<SaveClientRateLimitsResponse> => {
    try {
      const res = await axios.put(`/admin/rate-limits/clients/${clientId}/endpoints/${endpointId}/exceptions`, payload);
      if (res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to save client endpoint exceptions'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  return {
    fetchBannerAds,
    createBannerAd,
    editBannerAd,
    deleteBannerAd,
    fetchGlobalApiLimits,
    saveGlobalApiLimits,
    fetchClientApiLimits,
    saveClientEndpointOverride,
    saveClientEndpointExceptions
  };
};

export const useDeleteBannerAd = (config = {}) => {
  const { deleteBannerAd } = useSiteAdministrationApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const article = await deleteBannerAd(id);
      await queryClient.invalidateQueries(['banners']);
      return article;
    },
    ...config
  });
};

export const useEditBannerAd = (config = {}) => {
  const { editBannerAd } = useSiteAdministrationApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<CreateBannerAd> }) => {
      const banner = await editBannerAd(id, data);
      await queryClient.invalidateQueries(['banners']);
      return banner;
    },
    ...config
  });
};

export const useCreateBannerAd = (config = {}) => {
  const { createBannerAd } = useSiteAdministrationApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: FormData) => {
      const banner = await createBannerAd(data);
      await queryClient.invalidateQueries(['banners']);
      return banner;
    },
    ...config
  });
};

export const useBannerAds = (all: boolean, config = {}): UseQueryResult<IBannerAd[], AxiosError> => {
  const { fetchBannerAds } = useSiteAdministrationApi();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['banners'],
    queryFn: async (): Promise<IBannerAd[]> => {
      const banners = await fetchBannerAds(all);
      banners.forEach((banner) => {
        queryClient.setQueryData(['banners', banner.id], banner);
      });

      return banners;
    },
    ...config
  });
};

export const useBannerAd = (id: number, config = {}): UseQueryResult<IBannerAd, AxiosError> => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['banners', id],
    queryFn: () => {
      return queryClient.getQueryData(['banners', id]);
    },
    ...config
  });
};

export const useGlobalApiLimits = (config = {}): UseQueryResult<GlobalRateLimitsResponse, AxiosError> => {
  const { fetchGlobalApiLimits } = useSiteAdministrationApi();
  return useQuery({
    queryKey: ['globalApiLimits'],
    queryFn: async (): Promise<GlobalRateLimitsResponse> => {
      return await fetchGlobalApiLimits();
    },
    ...config
  });
};

export const useSaveGlobalApiLimits = (config = {}) => {
  const { saveGlobalApiLimits } = useSiteAdministrationApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SaveGlobalRateLimitsPayload) => {
      const result = await saveGlobalApiLimits(data);
      await queryClient.invalidateQueries(['globalApiLimits']);
      return result;
    },
    ...config
  });
};

export const useClientApiLimits = (
  clientId?: number,
  config = {}
): UseQueryResult<ClientRateLimitsResponse, AxiosError> => {
  const { fetchClientApiLimits } = useSiteAdministrationApi();
  return useQuery({
    queryKey: ['clientApiLimits', clientId],
    queryFn: async (): Promise<ClientRateLimitsResponse> => {
      return await fetchClientApiLimits(clientId!);
    },
    enabled: !!clientId,
    ...config
  });
};

export const useSaveClientEndpointOverride = (clientId?: number, config = {}) => {
  const { saveClientEndpointOverride } = useSiteAdministrationApi();

  return useMutation({
    mutationFn: async ({ endpointId, data }: { endpointId: number; data: SaveClientEndpointOverridePayload }) => {
      return await saveClientEndpointOverride(clientId!, endpointId, data);
    },
    ...config
  });
};

export const useSaveClientEndpointExceptions = (clientId?: number, config = {}) => {
  const { saveClientEndpointExceptions } = useSiteAdministrationApi();

  return useMutation({
    mutationFn: async ({ endpointId, data }: { endpointId: number; data: SaveClientEndpointExceptionsPayload }) => {
      return await saveClientEndpointExceptions(clientId!, endpointId, data);
    },
    ...config
  });
};
